import React from "react";
import toast from "react-hot-toast";
import { setDocData } from "../../../utils/firebase/Firebase.utils";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Modal from "../Components/Modal";
import { MdModeEdit, MdSave, MdCancel, MdWarning } from "react-icons/md";
import TenancyInformationForm from "./TenancyInformationForm";

export default function TenancyInformation({ tenant }) {
  const { id } = useParams();
  const [editMode, setEditMode] = React.useState(false);
  const [formData, setFormData] = React.useState(tenant);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = React.useState(false);

  const currentUser = useSelector((state) => state.userObject);

  const allowableAccess = ["admin", "maker", "super-admin"];
  const userRoles = currentUser.roles;

  const handleEditMode = () => {
    if (editMode) {
      // If in edit mode, show confirmation before canceling
      setShowConfirmCancel(true);
    } else {
      // Enter edit mode
      setEditMode(true);
    }
  };

  const handleConfirmCancel = (confirm) => {
    setShowConfirmCancel(false);
    if (confirm) {
      setFormData(tenant);
      setEditMode(false);
      toast.error("Changes Discarded");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (window.confirm("Are you sure you want to save the changes?")) {
      setIsProcessing(true);
      const docObject = {
        ...formData,
        updatedAt: new Date().toLocaleString(),
        updatedBy: currentUser.email,
      };

      try {
        await setDocData("tenants", id, docObject);
        toast.success("Changes Saved");
      } catch (error) {
        toast.error("Error saving changes");
        console.error(error);
      } finally {
        setIsProcessing(false);
        setEditMode(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#363062]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-[#363062]">
            Tenancy Details
          </h2>
          <p className="text-sm text-gray-500">Lease and Payment Information</p>
        </div>
        {allowableAccess.some((role) => userRoles.includes(role)) && (
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <button
                  type="button"
                  onClick={handleEditMode}
                  disabled={isProcessing}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MdCancel className="w-4 h-4 mr-1.5" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isProcessing}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-[#363062] rounded-md hover:bg-[#363062]/90 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1.5"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <MdSave className="w-4 h-4 mr-1.5" />
                      Save Changes
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleEditMode}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-[#363062] bg-[#363062]/10 rounded-md hover:bg-[#363062]/20 transition-colors duration-200"
              >
                <MdModeEdit className="w-4 h-4 mr-1.5" />
                Edit Information
              </button>
            )}
          </div>
        )}
      </div>

      {/* Form Component */}
      <TenancyInformationForm
        tenant={tenant}
        formData={formData}
        editMode={editMode}
        onChange={handleChange}
        onSubmit={handleSave}
      />

      {/* Cancel Confirmation Modal */}
      {showConfirmCancel && (
        <Modal>
          <div className="bg-white rounded-lg shadow-lg p-4 text-center max-w-xs mx-auto border border-gray-200">
            <div className="flex justify-center mb-2">
              <div className="rounded-full bg-red-100 p-2">
                <MdWarning className="w-6 h-6 text-red-500" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Discard Changes?
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Your changes will not be saved.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => handleConfirmCancel(false)}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Continue Editing
              </button>
              <button
                type="button"
                onClick={() => handleConfirmCancel(true)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-md hover:bg-red-600"
              >
                Discard Changes
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
